import React, {
  ReactElement, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { getTitle, NotFoundPage, waitForElement } from 'slovlex-idsk-react-components';
import routes from './routes';
import PredpisyPage from '../page/PredpisyPage/PredpisyPage';
import PredpisyCudziJazykPage from '../page/PredpisyCudziJazykPage/PredpisyCudziJazykPage';
import NajcastejsieOtazkyPage from '../page/NajcastejsieOtazkyPage/NajcastejsieOtazkyPage';
import PriruckyPage from '../page/Prirucky/PriruckyPage';
import InformacieOCookiesPage from '../page/InformacieOCookies/InformacieOCookiesPage';
import VyhlasenieOPristupnostiPage
  from '../page/VyhlasenieOPristupnosti/VyhlasenieOPristupnostiPage';
import KontaktPage from '../page/Kontakt/KontaktPage';
import AktualitaDetailPage from '../page/AktualitaDetailPage/AktualitaDetailPage';
import AktualitaListPage from '../page/AktualitaListPage/AktualitaListPage';
import NahlasenieChybyPage from '../page/NahlasenieChyby/NahlasenieChybyPage';
import VerzieModulovPage from '../page/VerzieModulov/VerzieModulovPage';
import VyhlasovanieVZbierkePage from '../page/VyhlasovanieVZbierke/VyhlasovanieVZbierkePage';
import HomePage from '../page/HomePage';
import { externalRoutesPrefixList } from '../constants/externalRoutesConstants';
import handleAbsoluteUrlRedirects from './absoluteUrlHandler';
import { useSecurityContext } from '../context/SecurityContext';
import Layout from '../page/Layout';

const StatickyObsahRouter = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorIsSettingRoute, setAnchorIsSettingRoute] = useState(true);
  // SLASLX-3400 (P2): ref so the layout effect synchronously skips echoing parent-driven nav.
  const parentDrivenRef = useRef(false);

  const { setAccessToken, setUserRoles } = useSecurityContext();

  const onMessage = (event): void => {
    const message = event?.data;

    if (message) {
      if (message.type && message.type === 'parent-to-iframe-internal-route') {
        parentDrivenRef.current = true;
        navigate(message.route, { replace: true });
      } else if (typeof message === 'string' && message.includes('accessToken=') && setAccessToken) {
        setAccessToken(message.substring('accessToken='.length));
      } else if (typeof message === 'string' && message.includes('userRoles=') && setUserRoles) {
        setUserRoles(message.substring('userRoles='.length).split(','));
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', onMessage);
    window.addEventListener('click', () => {
      window.parent.postMessage({ type: 'iframe-to-parent-clicked' }, '*');
    });
    window.parent.postMessage({ type: 'iframe-to-parent-initialized' }, '*');
    handleAbsoluteUrlRedirects();
    if (window.self === window.top) {
      window.location.replace(`${process.env.REACT_APP_PORTAL_MAIN_URL}`
        + `${location.pathname ? location.pathname : ''}`
        + `${location.search ? location.search : ''}${location.hash ? location.hash : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (externalRoutesPrefixList.some(prefix => location.pathname.startsWith(prefix))) {
      notifyParentExternalRoute();
      return;
    }
    if (parentDrivenRef.current) {
      parentDrivenRef.current = false;
      return;
    }
    if (document.referrer && document.referrer !== document.location.href
      && location.pathname !== routes.loading.to
      && anchorIsSettingRoute) {
      notifyParent();
      setAnchorIsSettingRoute(false);
    }

    if (anchorIsSettingRoute) {
      return;
    }
    notifyParent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash, location.search, location.state]);

  const notifyParent = (): void => {
    waitForElement('#app > *')
      .then(() => {
        window.parent.postMessage({
          type: 'iframe-to-parent-internal-route',
          route: (location.pathname ? location.pathname : '')
            + (location.search ? location.search : '')
            + (location.hash ? location.hash : ''),
          title: getTitle(),
        }, '*');
      });
  };

  const notifyParentExternalRoute = (): void => {
    window.parent.postMessage({
      type: 'iframe-to-parent-external-route',
      route: (location.pathname ? location.pathname : '')
        + (location.search ? location.search : '')
        + (location.hash ? location.hash : ''),
      title: getTitle(),
    }, '*');
  };

  return (
    <Routes>
      <Route path={routes.home.to} element={<HomePage />} />
      <Route path={routes.homeDomov.to} element={<HomePage />} />
      <Route
        path={routes.pravnePredpisy.to}
        element={
          <PredpisyPage />
        }
      />
      <Route path={routes.loading.to} element={null} />
      <Route path={routes.jazyk.to} element={<PredpisyCudziJazykPage />} />
      <Route path={routes.najcastejsieOtazky.to} element={<NajcastejsieOtazkyPage />} />
      <Route path={routes.prirucky.to} element={<PriruckyPage />} />
      <Route path={routes.nahlasenieChyby.to} element={<NahlasenieChybyPage />} />
      <Route path={routes.informacieOCookies.to} element={<InformacieOCookiesPage />} />
      <Route path={routes.verziaSlovlexu.to} element={<VerzieModulovPage />} />
      <Route path={routes.vyhlasenieOPristupnosti.to} element={<VyhlasenieOPristupnostiPage />} />
      <Route path={routes.vyhlasovanieVZbierke.to} element={<VyhlasovanieVZbierkePage />} />
      <Route path={routes.kontakt.to} element={<KontaktPage />} />
      <Route path={routes.aktuality.to} element={<AktualitaListPage />} />
      <Route path={routes.aktuality.detail.to} element={<AktualitaDetailPage />} />
      <Route path="*" element={<Layout><NotFoundPage withExternalRoute={false} /></Layout>} />
    </Routes>
  );
};

export default StatickyObsahRouter;
